import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LoadingState, ErrorState, TrustBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, MapPin, Building2, CheckCircle2, FileSearch, ShieldCheck, Camera, Mic, TriangleAlert, Info, Loader2, Pencil } from "lucide-react";
import { JobDetail } from "@/components/JobDetail";

const STEPS = ["Overview", "Profile", "Consent", "System check", "Assessment"];

export default function CandidateApply() {
  const { jobId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [candId, setCandId] = useState(null);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const [consent, setConsent] = useState({ data_processing: false, proctoring: false, alternative_path: false });
  const [perm, setPerm] = useState("idle");
  const [deviceCheck, setDeviceCheck] = useState({ camera: "Not checked", microphone: "Not checked" });

  useEffect(() => {
    Promise.all([api.get(`/jobs/${jobId}`), api.get("/me/applications")])
      .then(([j, apps]) => {
        setJob(j.data);
        const existing = (apps.data || []).find((a) => a.job_id === jobId);
        if (existing) {
          setCandId(existing.id);
          if (existing.consent) setConsent(existing.consent);
          const st = existing.assessment?.status;
          if (st === "scoring" || st === "completed") { nav("/candidate/applications"); return; }
          // Already in the assessment phase → send straight to the immersive exam room.
          if (st === "in_progress" || (existing.application_step || 0) >= 4) { nav(`/candidate/exam/${existing.id}`); return; }
          setStep(existing.application_step || 1);
        }
      })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [jobId]);

  useEffect(() => {
    if (candId && step > 0) api.patch(`/candidates/${candId}/progress`, { step }).catch(() => {});
  }, [step, candId]);

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${jobId}/apply`);
      setCandId(data.candidate_id);
      setStep(1);
    } catch { toast.error("Could not apply"); } finally { setBusy(false); }
  };

  const confirmProfile = async () => {
    setBusy(true);
    try { await api.post(`/candidates/${candId}/confirm-profile`); setStep(2); }
    finally { setBusy(false); }
  };

  const saveConsent = async () => {
    if (!consent.data_processing) { toast.error("Data processing consent is required to proceed"); return; }
    if (!consent.proctoring && !consent.alternative_path) { toast.error("Choose proctored or the alternative non-biometric path"); return; }
    setBusy(true);
    try { await api.post(`/candidates/${candId}/consent`, consent); setStep(3); }
    finally { setBusy(false); }
  };

  const requestPermissions = async () => {
    setPerm("checking");
    const policy = job?.proctoring_policy || {};
    const checks = await Promise.all([["camera", policy.require_camera !== false], ["microphone", !!policy.require_mic]].map(async ([kind, required]) => {
      if (!required) { setDeviceCheck((c)=>({...c,[kind]:"Not required"})); return true; }
      try {
        if (!navigator.mediaDevices?.getUserMedia) throw Object.assign(new Error(),{name:"Unsupported"});
        const stream = await navigator.mediaDevices.getUserMedia(kind === "camera" ? {video:true,audio:false} : {video:false,audio:true});
        stream.getTracks().forEach((t)=>t.stop());
        setDeviceCheck((c)=>({...c,[kind]:"Ready"})); return true;
      } catch (e) {
        const message = {NotAllowedError:"Permission denied — allow in site settings",NotFoundError:"No device connected",NotReadableError:"Device busy in another app",Unsupported:"Open the secure assessment link in a supported browser"}[e.name] || "Device check failed";
        setDeviceCheck((c)=>({...c,[kind]:message})); return false;
      }
    }));
    setPerm(checks.every(Boolean) ? "granted" : "denied");
  };

  const enterExamRoom = async () => {
    setBusy(true);
    try {
      await api.post(`/candidates/${candId}/consent`, consent);
      await api.post(`/candidates/${candId}/assessment`, { decision: "start" });
      nav(`/candidate/exam/${candId}`);
    } catch { toast.error("Could not open the assessment room"); }
    finally { setBusy(false); }
  };

  if (loading) return <LoadingState label="Loading role…" />;
  if (err) return <div className="p-8"><ErrorState message={err} /></div>;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <button onClick={() => nav("/candidate/jobs")} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="apply-back">
        <ArrowLeft className="h-4 w-4" /> Back to roles
      </button>

      {/* stepper */}
      <div className="space-y-2">
        <Progress value={((step + 1) / STEPS.length) * 100} className="h-1.5" />
        <div className="flex justify-between text-[11px] font-mono text-muted-foreground">
          {STEPS.map((s, i) => <span key={s} className={i <= step ? "text-primary" : ""}>{s}</span>)}
        </div>
      </div>

      {step === 0 && (
        <div className="space-y-4" data-testid="apply-overview">
          <JobDetail job={job} variant="candidate" />
          <div className="rounded-xl border border-border bg-card p-4">
            <Button className="w-full gap-1.5" onClick={apply} disabled={busy} data-testid="start-application">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Apply now <ArrowRight className="h-4 w-4" /></>}
            </Button>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="rounded-xl border border-border bg-card p-6 space-y-4" data-testid="apply-profile">
          <div className="flex items-center gap-2"><FileSearch className="h-5 w-5 text-primary" /><h2 className="text-xl font-display font-semibold">Confirm your parsed profile</h2></div>
          <TrustBadge type="ai">Auto-parsed from your CV — please verify</TrustBadge>
          <div className="space-y-3">
            {[
              { label: "Name", value: user?.name },
              { label: "Email", value: user?.email },
              { label: "Headline", value: `Candidate for ${job.title}` },
            ].map((f) => (
              <div key={f.label} className="flex items-center justify-between rounded-lg bg-muted/40 p-3">
                <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground">{f.label}</div><div className="text-sm">{f.value}</div></div>
                <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">If anything is wrong, you can correct it before we use it for assessment.</p>
          <Button className="w-full" onClick={confirmProfile} disabled={busy} data-testid="confirm-profile">Looks correct — continue</Button>
        </div>
      )}

      {step === 2 && (
        <div className="rounded-xl border border-border bg-card p-6 space-y-4" data-testid="apply-consent">
          <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" /><h2 className="text-xl font-display font-semibold">Your consent & choices</h2></div>
          <div className="space-y-3">
            <ConsentRow checked={consent.data_processing} onChange={(v) => setConsent((c) => ({ ...c, data_processing: v }))} testId="consent-data"
              title="Process my application data" required desc="We'll use your profile and responses to assess this application. You can request export or deletion anytime." />
            <ConsentRow checked={consent.proctoring} onChange={(v) => setConsent((c) => ({ ...c, proctoring: v, alternative_path: v ? false : c.alternative_path }))} testId="consent-proctoring"
              title="Enable advisory proctoring (camera & mic)" desc="Signals are ADVISORY ONLY and never treated as proof. A human always reviews outcomes. When something is flagged, a camera snapshot and, if talking is picked up, a short (~5 second) audio clip are saved as evidence. They are deleted with the rest of your test data at the end of the retention period." />
            <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-3 flex gap-2.5">
              <Info className="h-4 w-4 text-blue-400 shrink-0 mt-0.5" />
              <p className="text-xs text-blue-200 leading-relaxed">Prefer not to use your camera? Choose the <strong>alternative non-biometric path</strong> — your application will not be disadvantaged.</p>
            </div>
            <ConsentRow checked={consent.alternative_path} onChange={(v) => setConsent((c) => ({ ...c, alternative_path: v, proctoring: v ? false : c.proctoring }))} testId="consent-alternative"
              title="Use the alternative non-biometric path" desc="Complete the assessment without camera/mic. No identity signals collected." />
          </div>
          <Button className="w-full" onClick={saveConsent} disabled={busy} data-testid="save-consent">Save & continue</Button>
        </div>
      )}

      {step === 3 && (
        <div className="rounded-xl border border-border bg-card p-6 space-y-4" data-testid="apply-syscheck">
          <h2 className="text-xl font-display font-semibold">System check</h2>
          {consent.alternative_path ? (
            <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-4 flex gap-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" />
              <div><div className="text-sm font-medium text-emerald-300">Non-biometric path selected</div><p className="text-xs text-emerald-200/80">No camera or microphone required. You're ready to enter the assessment room.</p></div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className={`rounded-lg border p-4 text-center ${perm === "granted" ? "border-emerald-500/40 bg-emerald-500/10" : "border-border bg-muted/30"}`}>
                  <Camera className={`h-6 w-6 mx-auto mb-2 ${perm === "granted" ? "text-emerald-400" : "text-muted-foreground"}`} />
                  <div className="text-xs">Camera</div><p className="text-xs mt-2" data-testid="preflight-camera-status">{deviceCheck.camera}</p>
                </div>
                <div className={`rounded-lg border p-4 text-center ${perm === "granted" ? "border-emerald-500/40 bg-emerald-500/10" : "border-border bg-muted/30"}`}>
                  <Mic className={`h-6 w-6 mx-auto mb-2 ${perm === "granted" ? "text-emerald-400" : "text-muted-foreground"}`} />
                  <div className="text-xs">Microphone</div><p className="text-xs mt-2" data-testid="preflight-microphone-status">{deviceCheck.microphone}</p>
                </div>
              </div>
              {perm === "denied" && (
                <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-4 space-y-2" data-testid="perm-denied">
                  <div className="flex items-center gap-2 text-rose-300 text-sm font-medium"><TriangleAlert className="h-4 w-4" /> Camera/mic access blocked</div>
                  <p className="text-xs text-rose-200/80">No problem — you can retry after allowing access in your browser, or switch to the non-biometric path and continue without them.</p>
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" variant="outline" onClick={requestPermissions} data-testid="retry-perm">Retry</Button>
                    <Button size="sm" onClick={() => { setConsent((c) => ({ ...c, alternative_path: true, proctoring: false })); toast.success("Switched to non-biometric path"); }} data-testid="switch-alternative">Use non-biometric path</Button>
                  </div>
                </div>
              )}
              {perm === "idle" && <Button variant="outline" className="w-full gap-2" onClick={requestPermissions} data-testid="request-perm"><Camera className="h-4 w-4" /> Allow camera & microphone</Button>}
            </>
          )}
          <Button className="w-full gap-1.5" onClick={enterExamRoom} disabled={busy || (!consent.alternative_path && perm !== "granted")} data-testid="syscheck-continue">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Enter assessment room <ArrowRight className="h-4 w-4" /></>}
          </Button>
        </div>
      )}
    </div>
  );
}

function ConsentRow({ checked, onChange, title, desc, required, testId }) {
  return (
    <label className="flex items-start gap-3 rounded-lg border border-border p-3 cursor-pointer hover:bg-accent/40 transition-colors">
      <Checkbox checked={checked} onCheckedChange={onChange} className="mt-0.5" data-testid={testId} />
      <div>
        <div className="text-sm font-medium flex items-center gap-2">{title} {required && <span className="text-[10px] uppercase tracking-wider text-rose-400">required</span>}</div>
        <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
      </div>
    </label>
  );
}
